// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.
//
// The Owner row of the alarm Next step card: who owns the alarm and since when,
// with Assign to me / Take over / Release. Rendered only behind the
// alarm-owner-enabled flag.

import React, { useContext, useEffect, useRef, useState } from 'react';
import { Button, InlineLoading } from '@carbon/react';
import { AuthContext } from '../../contexts/AuthContext';
import { alarmAssignmentErrorMessage, useAlarmAssignment } from '../../hooks/useAlarmAssignment';
import { useSiteTime } from '../../hooks/useSiteTime';
import { sameAccount } from '../../components/shell/alarms/AlarmOwnerLine';
import type { AlarmAssignmentAction } from '../../services/alarmApiService';

/** The alarm on a re-read result, when the caller's refetch hands it back. */
function alarmOf(result: unknown): { owner?: string } | undefined {
  if (!result || typeof result !== 'object' || !('data' in result)) return undefined;
  const data = (result as { data?: unknown }).data;
  return data && typeof data === 'object' ? (data as { owner?: string }) : undefined;
}

/** How long the "Saved" confirmation stays beside the owner. */
const SAVED_MS = 3000;

interface Props {
  siteId: string;
  alarmId: string;
  /** The stored owner (AEM's assigned_to), if any. */
  owner?: string;
  /** When the owner was set (AEM's assigned_at), if known. */
  assignedAt?: string;
  /** A closed alarm keeps its owner on show but can no longer be claimed or released. */
  closed: boolean;
  /** Alarm re-fetch: the assignment call returns 204 with no body. */
  onAssigned: () => void | Promise<unknown>;
}

export default function AlarmOwnerRow({
  siteId,
  alarmId,
  owner,
  assignedAt,
  closed,
  onAssigned,
}: Props) {
  // Read the context directly: the card also renders outside an AuthProvider.
  const myEmail = useContext(AuthContext)?.user?.email;
  const { formatIn } = useSiteTime();
  const mutation = useAlarmAssignment();
  // "saving" covers the call and the re-read that follows it, so the stale stored
  // owner is never on screen in between; "saved" is the confirmation afterwards.
  const [phase, setPhase] = useState<'idle' | 'saving' | 'saved'>('idle');
  const [action, setAction] = useState<AlarmAssignmentAction>('assign_to_me');
  // Taking over someone else's alarm asks first; claiming a free one or releasing does not.
  const [confirming, setConfirming] = useState(false);
  // AEM accepted the write but the re-read did not show it, or failed.
  const [notice, setNotice] = useState<string | null>(null);
  const latestOwner = useRef(owner);
  latestOwner.current = owner;

  useEffect(() => {
    if (phase !== 'saved') return undefined;
    const t = setTimeout(() => setPhase('idle'), SAVED_MS);
    return () => clearTimeout(t);
  }, [phase]);

  function send(next: AlarmAssignmentAction) {
    setAction(next);
    setNotice(null);
    setPhase('saving');
    mutation.mutate(
      { siteId, alarmId, action: next },
      {
        // A 204 only says AEM took the write; the re-read is the proof. An
        // unchanged owner or a failed re-read is said so, never shown as Saved.
        onSuccess: () => {
          Promise.resolve(onAssigned()).then(
            result => {
              const read = alarmOf(result);
              const after = read ? read.owner : latestOwner.current;
              const ok = next === 'release' ? !after : sameAccount(after, myEmail);
              if (!ok) {
                setNotice(
                  `AEM accepted the ${next === 'release' ? 'release' : 'assignment'} but the alarm still shows ${after ?? 'no owner'}. Try again, and tell the alarms team if it keeps happening.`
                );
              }
              setPhase(ok ? 'saved' : 'idle');
            },
            () => {
              setNotice(
                'The assignment was accepted, but the alarm could not be re-read. Reload to see who holds it.'
              );
              setPhase('idle');
            }
          );
        },
        onError: () => setPhase('idle'),
      }
    );
  }

  const mine = sameAccount(owner, myEmail);
  // A closed alarm keeps its actions on show, disabled, so the operator sees why.
  const blocked = closed ? 'This alarm is closed.' : undefined;
  const since = assignedAt && owner ? formatIn(siteId, assignedAt, 'clock', '') : '';

  return (
    <div className="ald-ns-owner">
      <span className="ald-ns-owner-label">Owner</span>
      {phase === 'saving' ? (
        <InlineLoading className="ald-ns-owner-saving" description="Saving…" status="active" />
      ) : (
        <span className="ald-ns-owner-line">
          <span
            className={`ald-ns-owner-value${owner ? '' : ' ald-ns-owner-value--unassigned'}`}
            title={owner && !mine ? owner : undefined}
          >
            {mine ? 'You' : (owner ?? 'Unassigned')}
          </span>
          {phase === 'saved' && (
            <span className="ald-ns-owner-saved" role="status">
              Saved
            </span>
          )}
          {since && <span className="ald-ns-owner-since">· since {since}</span>}
        </span>
      )}
      {phase !== 'saving' && !confirming && (
        <span className="ald-ns-owner-actions">
          {!owner && (
            <Button
              kind="ghost"
              size="sm"
              disabled={closed}
              title={blocked}
              onClick={() => send('assign_to_me')}
            >
              Assign to me
            </Button>
          )}
          {owner && !mine && (
            <Button
              kind="ghost"
              size="sm"
              disabled={closed}
              title={blocked}
              onClick={() => setConfirming(true)}
            >
              Take over
            </Button>
          )}
          {owner && mine && (
            <Button
              kind="ghost"
              size="sm"
              disabled={closed}
              title={blocked}
              onClick={() => send('release')}
            >
              Release
            </Button>
          )}
        </span>
      )}
      {!closed && confirming && (
        <span className="ald-ns-owner-actions">
          <span className="ald-actionbar-note">Take over this alarm from {owner}?</span>
          <Button
            kind="primary"
            size="sm"
            onClick={() => {
              setConfirming(false);
              send('assign_to_me');
            }}
          >
            Confirm
          </Button>
          <Button kind="ghost" size="sm" onClick={() => setConfirming(false)}>
            Cancel
          </Button>
        </span>
      )}
      {notice && (
        <p className="ald-ns-warning" role="alert">
          {notice}
        </p>
      )}
      {mutation.isError && phase === 'idle' && (
        <p className="ald-wo-cmms-error" role="alert">
          {alarmAssignmentErrorMessage(mutation.error, action)}
        </p>
      )}
    </div>
  );
}
